/**
 * Client-side cache for /api/live_market (members only).
 * - Fetches full quote map from server every 180s (NY 04:00–20:00: premarket, regular, afterhours)
 * - Re-applies live fields to UI from cache every 180s
 * - Regular-session quotes expire after 180s; extended (premarket/afterhours) quotes
 *   persist — the last updated extended price is kept until a new update arrives.
 */

import { isNyMarketOpen, isNyLiveWindow, getNyMarketSession } from './marketHours';
import { authFetch, isAuthenticated } from './authFetch';

export const LIVE_MARKET_CLIENT_CACHE_KEY = 'liveMarketQuotesCache';
/** Survives regular-cache TTL/reload clears so Movers (and others) keep last extended snapshot. */
export const LIVE_MARKET_EXTENDED_CACHE_KEY = 'liveMarketExtendedQuotesCache';
/** Fetch server cache and update localStorage */
export const LIVE_MARKET_CACHE_FETCH_MS = 180 * 1000;
/** Re-apply live fields to tables from client cache (read + display interval) */
export const LIVE_MARKET_DISPLAY_REFRESH_MS = 180 * 1000;

const SERVER_READY_POLL_MS = 1000;
const SERVER_READY_MAX_ATTEMPTS = 60;

let memoryEntry = null;
let fetchIntervalId = null;
let displayIntervalId = null;
let inFlightFetch = null;
const displayListeners = new Set();

const hasSessionQuotes = (sessionEntry) =>
  Boolean(sessionEntry && sessionEntry.quotes && Object.keys(sessionEntry.quotes).length > 0);

const hasQuotesMap = (quotes) =>
  Boolean(quotes && typeof quotes === 'object' && Object.keys(quotes).length > 0);

const mergeRegularQuotes = (prev, next) => {
  if (hasQuotesMap(next)) return next;
  if (hasQuotesMap(prev)) return prev;
  return next && typeof next === 'object' ? next : {};
};

const readJsonKey = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const writeJsonKey = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.warn(`[live_market client cache] localStorage write failed (${key}):`, e.message);
  }
};

const readPersistedExtended = () => {
  const parsed = readJsonKey(LIVE_MARKET_EXTENDED_CACHE_KEY);
  if (!parsed || typeof parsed !== 'object') return { premarket: null, afterhours: null };
  return {
    premarket: hasSessionQuotes(parsed.premarket) ? parsed.premarket : null,
    afterhours: hasSessionQuotes(parsed.afterhours) ? parsed.afterhours : null,
  };
};

const writePersistedExtended = (extended) => {
  const payload = {
    premarket: hasSessionQuotes(extended?.premarket) ? extended.premarket : null,
    afterhours: hasSessionQuotes(extended?.afterhours) ? extended.afterhours : null,
    updatedAt: Date.now(),
  };
  if (!payload.premarket && !payload.afterhours) return;
  writeJsonKey(LIVE_MARKET_EXTENDED_CACHE_KEY, payload);
};

/**
 * Keep last non-empty premarket/afterhours snapshot when the server omits a session.
 * @param {object|null} prev
 * @param {object|null} next
 */
const mergeExtended = (prev, next) => {
  const prevSafe = prev && typeof prev === 'object' ? prev : {};
  const nextSafe = next && typeof next === 'object' ? next : {};
  return {
    premarket: hasSessionQuotes(nextSafe.premarket)
      ? nextSafe.premarket
      : (hasSessionQuotes(prevSafe.premarket) ? prevSafe.premarket : null),
    afterhours: hasSessionQuotes(nextSafe.afterhours)
      ? nextSafe.afterhours
      : (hasSessionQuotes(prevSafe.afterhours) ? prevSafe.afterhours : null),
  };
};

const readFromLocalStorage = () => {
  try {
    const parsed = readJsonKey(LIVE_MARKET_CLIENT_CACHE_KEY);
    if (!parsed || typeof parsed.timestamp !== 'number') return null;
    if (!parsed.quotes && !parsed.extended) return null;
    const persistedExt = readPersistedExtended();
    parsed.extended = mergeExtended(persistedExt, parsed.extended);
    return parsed;
  } catch {
    return null;
  }
};

const writeToLocalStorage = (entry) => {
  try {
    localStorage.setItem(LIVE_MARKET_CLIENT_CACHE_KEY, JSON.stringify(entry));
  } catch (e) {
    console.warn('[live_market client cache] localStorage write failed:', e.message);
  }
  if (entry?.extended) writePersistedExtended(entry.extended);
};

const filterQuotes = (quotes, symbolIds) => {
  if (!quotes || !Array.isArray(symbolIds) || symbolIds.length === 0) return quotes || {};
  const out = {};
  for (const id of symbolIds) {
    const key = String(id).trim();
    if (key && quotes[key]) out[key] = quotes[key];
  }
  return out;
};

/** Regular-session quotes are only valid while fresh and during regular hours. */
const areRegularQuotesValid = (entry) => {
  if (!entry || !entry.quotes || Object.keys(entry.quotes).length === 0) return false;
  if (!isNyMarketOpen()) return false;
  return Date.now() - entry.timestamp < LIVE_MARKET_DISPLAY_REFRESH_MS;
};

/**
 * Extended quotes for display: the current session's map during premarket/afterhours,
 * otherwise the most recently updated one (persisted last data).
 * @returns {{ quotes: Object, cachedAt: number, tradingDay: string|null }|null}
 */
const pickDisplayExtended = (entry) => {
  const ext = entry && entry.extended;
  if (!ext) return null;
  const session = getNyMarketSession();
  if (session === 'premarket' && hasSessionQuotes(ext.premarket)) return ext.premarket;
  if (session === 'afterhours' && hasSessionQuotes(ext.afterhours)) return ext.afterhours;
  const pre = hasSessionQuotes(ext.premarket) ? ext.premarket : null;
  const aft = hasSessionQuotes(ext.afterhours) ? ext.afterhours : null;
  if (pre && aft) return (aft.cachedAt || 0) >= (pre.cachedAt || 0) ? aft : pre;
  return aft || pre;
};

const loadEntry = () => {
  if (memoryEntry) {
    memoryEntry.extended = mergeExtended(readPersistedExtended(), memoryEntry.extended);
    return memoryEntry;
  }
  const stored = readFromLocalStorage();
  if (stored) {
    memoryEntry = stored;
    return stored;
  }
  // Regular cache may have been cleared; still restore last extended snapshot.
  const persistedExt = readPersistedExtended();
  if (hasSessionQuotes(persistedExt.premarket) || hasSessionQuotes(persistedExt.afterhours)) {
    memoryEntry = {
      timestamp: 0,
      cachedAt: persistedExt.afterhours?.cachedAt || persistedExt.premarket?.cachedAt || Date.now(),
      cacheTtlMs: LIVE_MARKET_CACHE_FETCH_MS,
      session: getNyMarketSession(),
      quotes: {},
      extended: persistedExt,
    };
    return memoryEntry;
  }
  return null;
};

export const notifyLiveMarketDisplayUpdate = () => {
  if (!isAuthenticated()) return;
  displayListeners.forEach((fn) => {
    try {
      fn();
    } catch (e) {
      console.warn('[live_market client cache] display listener error:', e.message);
    }
  });
};

async function waitForServerReady() {
  for (let attempt = 0; attempt < SERVER_READY_MAX_ATTEMPTS; attempt++) {
    try {
      const res = await fetch('/api/ready');
      if (res.ok) {
        const body = await res.json();
        if (body?.ready) return true;
      }
    } catch {
      /* server still starting */
    }
    await new Promise((resolve) => setTimeout(resolve, SERVER_READY_POLL_MS));
  }
  console.warn('[live_market client] server ready check timed out');
  return false;
}

/**
 * Fetch full live quote map (regular + extended) from server and persist to client cache.
 * @param {{ force?: boolean }} [options] - force fetches even outside the 04:00–20:00 window
 *   (used at startup to restore persisted premarket/afterhours data).
 */
export async function refreshLiveMarketClientCache(options = {}) {
  const force = options.force === true;
  if (!isAuthenticated()) return null;
  if (!force && !isNyLiveWindow()) return null;
  if (inFlightFetch) return inFlightFetch;

  inFlightFetch = (async () => {
    try {
      const res = await authFetch('/api/live_market');
      if (!res.ok) return null;
      const body = await res.json();
      if (!body || !body.available) return null;

      const prevStored = memoryEntry || readFromLocalStorage();
      const prevExtended = mergeExtended(
        readPersistedExtended(),
        prevStored?.extended
      );
      const entry = {
        timestamp: Date.now(),
        cachedAt: body.cachedAt || Date.now(),
        cacheTtlMs: body.cacheTtlMs || LIVE_MARKET_CACHE_FETCH_MS,
        session: body.session || getNyMarketSession(),
        quotes: mergeRegularQuotes(prevStored?.quotes, body.quotes),
        extended: mergeExtended(prevExtended, body.extended),
      };
      memoryEntry = entry;
      writeToLocalStorage(entry);
      notifyLiveMarketDisplayUpdate();
      return entry;
    } catch (e) {
      console.warn('[live_market client cache] refresh failed:', e.message);
      return null;
    } finally {
      inFlightFetch = null;
    }
  })();

  return inFlightFetch;
}

/**
 * Read live quotes from client cache (sync). Optional symbol_id filter.
 * - quotes: regular-session quotes ({} outside regular hours or when stale)
 * - lastRegularQuotes: persisted last regular snapshot (weekends / holidays)
 * - extendedQuotes: current/most recent premarket or afterhours quotes (persisted)
 * @returns {{ quotes: Object, lastRegularQuotes: Object, extendedQuotes: Object, premarketQuotes: Object,
 *   afterhoursQuotes: Object, session: string, cachedAt: number, cacheTtlMs: number,
 *   available: boolean }|null}
 */
export function getLiveMarketQuotesFromClientCache(symbolIds = null) {
  if (!isAuthenticated()) return null;
  const entry = loadEntry();
  if (!entry) return null;

  const quotes = areRegularQuotesValid(entry) ? filterQuotes(entry.quotes, symbolIds) : {};
  const lastRegularQuotes = filterQuotes(entry.quotes, symbolIds);
  const displayExt = pickDisplayExtended(entry);
  const extendedQuotes = displayExt ? filterQuotes(displayExt.quotes, symbolIds) : {};
  const premarketQuotes = entry.extended?.premarket
    ? filterQuotes(entry.extended.premarket.quotes, symbolIds)
    : {};
  const afterhoursQuotes = entry.extended?.afterhours
    ? filterQuotes(entry.extended.afterhours.quotes, symbolIds)
    : {};

  if (
    Object.keys(quotes).length === 0 &&
    Object.keys(lastRegularQuotes).length === 0 &&
    Object.keys(extendedQuotes).length === 0 &&
    Object.keys(premarketQuotes).length === 0 &&
    Object.keys(afterhoursQuotes).length === 0
  ) {
    return null;
  }

  return {
    quotes,
    lastRegularQuotes,
    extendedQuotes,
    premarketQuotes,
    afterhoursQuotes,
    session: getNyMarketSession(),
    cachedAt: entry.cachedAt,
    cacheTtlMs: entry.cacheTtlMs || LIVE_MARKET_CACHE_FETCH_MS,
    available: true,
  };
}

/**
 * Subscribe to display refresh (every 180s globally + on cache write + immediate on subscribe).
 * @param {() => void} callback
 * @returns {() => void} unsubscribe
 */
export function subscribeLiveMarketDisplay(callback) {
  if (typeof callback !== 'function') return () => {};
  displayListeners.add(callback);
  if (isAuthenticated() && loadEntry()) {
    try {
      callback();
    } catch (e) {
      console.warn('[live_market client cache] display listener error:', e.message);
    }
  }
  return () => displayListeners.delete(callback);
}

/**
 * Start global client cache fetch (180s) and display notify (180s) intervals.
 * Waits for GET /api/ready before the first fetch. The first fetch always runs
 * (to restore persisted extended data); interval fetches run only 04:00–20:00 ET.
 * @returns {() => void} stop
 */
export function startLiveMarketClientCache() {
  let cancelled = false;

  const runFetch = (force = false) => {
    if (!isAuthenticated()) return;
    if (!force && !isNyLiveWindow()) return;
    refreshLiveMarketClientCache({ force });
  };

  const runDisplay = () => {
    notifyLiveMarketDisplayUpdate();
  };

  const startIntervals = () => {
    if (cancelled) return;
    runFetch(true);
    fetchIntervalId = setInterval(() => runFetch(false), LIVE_MARKET_CACHE_FETCH_MS);
    displayIntervalId = setInterval(runDisplay, LIVE_MARKET_DISPLAY_REFRESH_MS);
    console.log(
      `[live_market client] started: fetch every ${LIVE_MARKET_CACHE_FETCH_MS / 1000}s, ` +
      `display every ${LIVE_MARKET_DISPLAY_REFRESH_MS / 1000}s (NY 04:00–20:00, members only)`
    );
  };

  waitForServerReady().then((ready) => {
    if (cancelled || !ready) return;
    startIntervals();
  });

  return () => {
    cancelled = true;
    if (fetchIntervalId) clearInterval(fetchIntervalId);
    if (displayIntervalId) clearInterval(displayIntervalId);
    fetchIntervalId = null;
    displayIntervalId = null;
  };
}

/**
 * Clear regular live-market client cache.
 * @param {{ clearExtended?: boolean }} [options] - also wipe persisted premarket/afterhours
 *   (use on logout). Default keeps last extended snapshot for Movers / extended UI.
 */
export function clearLiveMarketClientCache(options = {}) {
  const clearExtended = options.clearExtended === true;
  memoryEntry = null;
  try {
    localStorage.removeItem(LIVE_MARKET_CLIENT_CACHE_KEY);
  } catch { /* ignore */ }
  if (clearExtended) {
    try {
      localStorage.removeItem(LIVE_MARKET_EXTENDED_CACHE_KEY);
    } catch { /* ignore */ }
  }
}
